/**
 * ArticleQuickCreateDialog - Minimal dialog for quick KB article creation
 *
 * Following the Quick Create Dialog pattern:
 * - 3-5 fields maximum (Title, Summary, Slug, Category)
 * - Redirects to detail page after creation for full editing
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { DialogForm } from '../../../shared/components/common';
import { useApiError } from '../../../shared/hooks/useApiError';
import { useCreateKBArticle, useKBCategories } from '../hooks/useKnowledgeBase';
import type { KBArticle } from '../types';

interface ArticleQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill category from context */
  defaultCategoryId?: string;
  /** Callback when article is created (before navigation) */
  onCreated?: (article: KBArticle) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
}

interface QuickCreateFormData {
  title: string;
  summary: string;
  slug: string;
  category_id?: string;
}

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 100);
}

export function ArticleQuickCreateDialog({
  open,
  onClose,
  defaultCategoryId,
  onCreated,
  skipNavigation = false,
}: ArticleQuickCreateDialogProps) {
  const { t } = useTranslation(['support', 'common']);
  const navigate = useNavigate();
  const createMutation = useCreateKBArticle();
  const { data: categoriesData } = useKBCategories();
  const { handleMutationError } = useApiError({ showToast: false });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      title: '',
      summary: '',
      slug: '',
      category_id: defaultCategoryId || undefined,
    },
  });

  const title = watch('title');

  // Auto-generate slug from title unless manually edited
  useEffect(() => {
    if (!slugManuallyEdited && title) {
      setValue('slug', generateSlug(title));
    }
  }, [title, slugManuallyEdited, setValue]);

  useEffect(() => {
    if (open) {
      reset({
        title: '',
        summary: '',
        slug: '',
        category_id: defaultCategoryId || undefined,
      });
      setSlugManuallyEdited(false);
    }
  }, [open, defaultCategoryId, reset]);

  const onSubmit = async (data: QuickCreateFormData) => {
    const createData = {
      title: data.title,
      slug: data.slug || generateSlug(data.title),
      body_markdown: '', // Empty body, will be filled on detail page
      summary: data.summary || undefined,
      category_id: data.category_id || undefined,
      is_internal: false,
    };

    try {
      const newArticle = await createMutation.mutateAsync(createData);
      onCreated?.(newArticle);
      onClose();

      if (!skipNavigation) {
        navigate(`/support/kb/articles/${newArticle.id}`);
      }
    } catch (error: unknown) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('kb.createArticle')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      cancelLabel={t('common:app.cancel')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="title">{t('kb.articleTitle')} *</Label>
          <Controller
            name="title"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <Input
                {...field}
                id="title"
                autoFocus
                placeholder={t('kb.titlePlaceholder')}
                className={cn(errors.title && 'border-destructive')}
              />
            )}
          />
          {errors.title && (
            <p className="text-sm text-destructive">{errors.title.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="summary">{t('kb.summary')}</Label>
          <Controller
            name="summary"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                id="summary"
                placeholder={t('kb.summaryPlaceholder')}
                rows={2}
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="slug">{t('kb.slug')} *</Label>
          <Controller
            name="slug"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <Input
                {...field}
                id="slug"
                placeholder={t('kb.slugPlaceholder')}
                className={cn(errors.slug && 'border-destructive')}
                onChange={(e) => {
                  setSlugManuallyEdited(true);
                  field.onChange(e);
                }}
              />
            )}
          />
          {errors.slug && (
            <p className="text-sm text-destructive">{errors.slug.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            {t('kb.slugHint')}
          </p>
        </div>

        <div className="space-y-2">
          <Label>{t('kb.category')}</Label>
          <Controller
            name="category_id"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || 'none'}
                onValueChange={(v) => field.onChange(v === 'none' ? undefined : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('common:app.select')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('common:app.none')}</SelectItem>
                  {categoriesData?.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          {t('kb.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
